/**
 * Canonical (markdown-native) copy/export rendering — the named `/core` R3
 * pilot (OpenSpec `monorepo-kit-editor` task 8.2).
 *
 * These are the pure half of `src/helpers/copyHelpers/copyCanonical.ts`: stored
 * markdown in, toggle-applied HTML (and its markdown sibling) out, with no
 * clipboard, no logging and no analytics. `copyCanonicalNote` stays app-side —
 * it performs the clipboard write and reaches the legacy `reformatHtml`
 * paste-shaping helper, neither of which may enter the kernel.
 */
import {
  renderHtmlToMarkdown,
  renderMarkdownToHtml,
} from '../renderer/canonical-renderer';
import { applyCopySettingsToHtml } from './apply-copy-settings';
import { type CopySettings } from './copy-settings';
import { detectCopyFormattingLoss, type CopyFormattingLoss } from './formatting-loss';

export type CanonicalCopyArgs = {
  markdownContent: string;
  settings: CopySettings;
};

export type CanonicalRenderResult = {
  html: string;
  markdown: string;
};

// Formatting-loss measurement belongs to the transform's own overloads only, so
// export/print (`renderCanonicalHtmlWithSettings`) and `prepareCopyPayload`
// neither accept the opt-in nor expose the result. Opt in only when the caller
// consumes the metadata; `formattingLoss` contains no note content.
type MeasuredRenderResult = CanonicalRenderResult & {
  formattingLoss: CopyFormattingLoss;
};
type MaybeMeasuredRenderResult = CanonicalRenderResult & {
  formattingLoss?: CopyFormattingLoss;
};

/**
 * The one copy-settings pipeline shared by copy and export/print, so the two
 * cannot drift. `sourceHtml` is the untoggled render, kept for loss detection.
 */
const renderCanonicalHtmlPair = ({
  markdownContent,
  settings,
}: CanonicalCopyArgs): { sourceHtml: string; html: string } => {
  const sourceHtml = renderMarkdownToHtml(markdownContent);
  return { sourceHtml, html: applyCopySettingsToHtml(sourceHtml, settings) };
};

/**
 * Render stored markdown to toggle-applied, editor-parity HTML through the
 * markdown-native pipeline. HTML-only — for export/print, which never need the
 * markdown sibling (no second headless round-trip).
 */
export const renderCanonicalHtmlWithSettings = (args: CanonicalCopyArgs): string =>
  renderCanonicalHtmlPair(args).html;

/**
 * Canonical sibling of `transformHtmlWithSettings`. Renders stored markdown to
 * editor-parity HTML, applies the toggles, then serialises back to markdown for
 * the `text/plain` payload — both directions go through the markdown-native
 * pipeline, never the legacy regex serialisers.
 *
 * No dash rebuild or `removeAnnotationFromMarkdown`: canonical HTML has semantic
 * `<ul>`/`<ol>` the DOM walk flattens directly, and an all-off pass strips marks
 * structurally before re-serialising.
 */
export function transformCanonicalMarkdownWithSettings(
  args: CanonicalCopyArgs & { includeFormattingLoss: true }
): MeasuredRenderResult;
export function transformCanonicalMarkdownWithSettings(
  args: CanonicalCopyArgs & { includeFormattingLoss?: false }
): CanonicalRenderResult;
export function transformCanonicalMarkdownWithSettings(
  args: CanonicalCopyArgs & { includeFormattingLoss?: boolean }
): MaybeMeasuredRenderResult;
export function transformCanonicalMarkdownWithSettings(
  args: CanonicalCopyArgs & { includeFormattingLoss?: boolean }
): MaybeMeasuredRenderResult {
  const { sourceHtml, html } = renderCanonicalHtmlPair(args);
  return {
    html,
    markdown: renderHtmlToMarkdown(html),
    ...(args.includeFormattingLoss
      ? { formattingLoss: detectCopyFormattingLoss(sourceHtml, html) }
      : {}),
  };
}
